import Seo from "../../components/Seo";
import { Reveal, Eyebrow, PageHero, PhotoFrame, CtaBand } from "../../components/Shared";
import { PAINT_CORRECTION, photo, fmt } from "../../data/site";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export default function PaintCorrection() {
  return (
    <>
      <Seo
        title="2-Step Paint Correction in Sioux Falls, SD | From $400 — A+ Detailing"
        description="2-step paint correction in Sioux Falls from $400: machine compounding and polishing that removes swirl marks, scratches and oxidation for a deep, mirror gloss finish."
        path="/paint-correction"
      />
      <PageHero
        eyebrow="Paint Correction"
        lines={["SWIRLS.", "GONE."]}
        lead="Our 2-step paint correction restores and enhances your vehicle's paintwork by removing imperfections such as swirl marks, scratches and oxidation — machine work, done by hand-operated precision."

        chips={[`From ${fmt(PAINT_CORRECTION.price)}`, "2-Step process"]}
        testPrefix="correction"
      />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <Reveal><Eyebrow>The 2-step process</Eyebrow></Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Cut, then polish.</h2>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-5 text-steel leading-relaxed">{PAINT_CORRECTION.blurb}</p>
            </Reveal>
            <div className="mt-10 space-y-5">
              {PAINT_CORRECTION.stages.map((s, i) => (
                <Reveal key={s.name} delay={0.1 + i * 0.1}>
                  <div className="flex gap-5 rounded-2xl border border-white/10 bg-card p-6" data-testid={`correction-stage-${i}`}>
                    <span className="font-mono text-sm text-bright pt-1">0{i + 1}</span>
                    <div>
                      <h3 className="font-display text-xl font-bold">{s.name}</h3>
                      <p className="mt-1.5 text-sm text-steel leading-relaxed">{s.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <div className="space-y-4">
            <Reveal delay={0.1}>
              <div className="rounded-2xl border border-white/10 bg-card p-9" data-testid="correction-removes">
                <Eyebrow>We remove</Eyebrow>
                <ul className="mt-6 space-y-4">
                  {["Swirl marks", "Light scratches", "Oxidation"].map((x) => (
                    <li key={x} className="flex items-center gap-3 text-steel">
                      <span className="h-1.5 w-1.5 rounded-full bg-bright" /> {x}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="rounded-2xl border border-bright/40 bg-gradient-to-br from-bright/15 to-card p-9" data-testid="correction-price-panel">
                <p className="font-mono text-[11px] uppercase tracking-widest text-bright">2-Step Correction</p>
                <p className="mt-3 font-display text-5xl font-extrabold">From {fmt(PAINT_CORRECTION.price)}</p>
                <Link to="/book" data-testid="correction-book-button" className="mt-7 flex w-full items-center justify-center rounded-full bg-bright hover:bg-accent text-white font-semibold px-7 py-3.5 transition-colors">
                  Book Paint Correction
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-coal border-y border-white/5 py-20 sm:py-28">
        <div className="mx-auto max-w-3xl px-5 sm:px-8 text-center">
          <Reveal>
            <Eyebrow className="text-center">Before you coat</Eyebrow>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
              Correction first. Protection second.
            </h2>
            <p className="mt-5 text-steel leading-relaxed">
              Ceramic coatings lock in whatever is underneath them. Correcting the paint
              first means your System X coating seals in a flawless, swirl-free finish.
            </p>
            <Link
              to="/ceramic-coatings"
              data-testid="correction-ceramic-link"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-bright hover:bg-accent text-white font-semibold px-7 py-3.5 transition-colors"
            >
              Explore Ceramic Coatings <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      <CtaBand testPrefix="correction" />
    </>
  );
}
